import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { mapStorageFailure } from '../errors.js'
import { assertCondition, type RecordQuery, type RecordStore } from '../stores.js'
import type { RevisionCondition, StoredRecord, VaultRecordKind } from '../types.js'

/**
 * IndexedDB record adapter (records via `idb`). The revision check and write
 * share one readwrite transaction, so simultaneous tabs cannot both commit the
 * same expected revision (LOC-03).
 */

export interface VaultDbSchema extends DBSchema {
  records: {
    key: string
    value: StoredRecord
    indexes: {
      'by-kind': VaultRecordKind
      /** `[kind, committed_at]` — the vault's list order, as an IndexedDB key. */
      'by-kind-committed': [VaultRecordKind, string]
      /** Completion records only; the document ID is an opaque local record ID. */
      'by-completion-document-committed': [VaultRecordKind, string, string]
    }
  }
  meta: {
    key: string
    value: unknown
  }
}

export const VAULT_DB_NAME = 'signbento-vault'

/**
 * Version 2 added bounded recency reads. Version 3 adds an index over the document ID stored in
 * new completion record headers. Older encrypted completion headers are backfilled by the vault
 * core once; the versionchange transaction alone cannot inspect their encrypted payloads.
 */
export const VAULT_DB_VERSION = 3
export const SCHEMA_VERSION_META_KEY = 'schema_version'

/** Above every ISO-8601 `committed_at`, so a `prev` cursor starts at the newest record. */
const ABOVE_ALL_TIMESTAMPS = '\uffff'

/**
 * The one IndexedDB global this adapter needs that the package's TypeScript `lib` does not carry
 * (no DOM lib, and `idb`'s own types are skipped). The runtime value is always the platform's
 * `IDBKeyRange` — the browser's, or `fake-indexeddb`'s in tests — so the declaration is only about
 * the type, and `never` is the one return type both builds accept: this package's build sees an
 * unresolvable `IDBKeyRange` where `never` is assignable, and the app build sees the real DOM type
 * where it is assignable too. A bare key cannot stand in for a range: IndexedDB converts a key into
 * a range containing *only* that key, so an upper-bound range is what makes "newest first, then stop
 * at the kind boundary" expressible at all.
 */
declare const IDBKeyRange: {
  upperBound(upper: [VaultRecordKind, string]): never
  upperBound(upper: [VaultRecordKind, string, string]): never
}

export class IdbRecordStore implements RecordStore {
  private constructor(readonly database: IDBPDatabase<VaultDbSchema>) {}

  static async open(name = VAULT_DB_NAME): Promise<IdbRecordStore> {
    const database = await openDB<VaultDbSchema>(name, VAULT_DB_VERSION, {
      upgrade(db, _oldVersion, _newVersion, transaction) {
        if (!db.objectStoreNames.contains('records')) {
          const records = db.createObjectStore('records', { keyPath: 'key' })
          records.createIndex('by-kind', 'kind')
          records.createIndex('by-kind-committed', ['kind', 'committed_at'])
          records.createIndex('by-completion-document-committed', [
            'kind',
            'completion_document_id',
            'committed_at',
          ])
        } else {
          const records = transaction.objectStore('records')
          if (!records.indexNames.contains('by-kind-committed')) {
            records.createIndex('by-kind-committed', ['kind', 'committed_at'])
          }
          if (!records.indexNames.contains('by-completion-document-committed')) {
            records.createIndex('by-completion-document-committed', [
              'kind',
              'completion_document_id',
              'committed_at',
            ])
          }
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta')
        }
      },
    })
    return new IdbRecordStore(database)
  }

  async get(key: string): Promise<StoredRecord | null> {
    return (await this.database.get('records', key)) ?? null
  }

  async put(record: StoredRecord, condition: RevisionCondition): Promise<void> {
    try {
      const tx = this.database.transaction('records', 'readwrite')
      const current = (await tx.store.get(record.key)) ?? null
      assertCondition(condition, current, record.key)
      await tx.store.put(record)
      await tx.done
    } catch (error) {
      throw mapStorageFailure(error, `could not commit record ${record.key}`)
    }
  }

  async delete(key: string, condition: RevisionCondition): Promise<void> {
    try {
      const tx = this.database.transaction('records', 'readwrite')
      const current = (await tx.store.get(key)) ?? null
      assertCondition(condition, current, key)
      await tx.store.delete(key)
      await tx.done
    } catch (error) {
      throw mapStorageFailure(error, `could not delete record ${key}`)
    }
  }

  async list(kind?: VaultRecordKind): Promise<StoredRecord[]> {
    if (kind === undefined) return this.database.getAll('records')
    return this.database.getAllFromIndex('records', 'by-kind', kind)
  }

  async count(kind?: VaultRecordKind): Promise<number> {
    if (kind === undefined) return this.database.count('records')
    return this.database.countFromIndex('records', 'by-kind', kind)
  }

  /**
   * Seeks with a cursor instead of materializing the kind: `advance` moves over the skipped
   * records without reading them, and the loop stops at `limit`. The `prev` direction over
   * `(kind, committed_at)` is the vault's list order; the loop stops at the kind boundary so a
   * query never walks into another kind's records.
   */
  async query(query: RecordQuery): Promise<StoredRecord[]> {
    const limit = query.limit === undefined ? Number.POSITIVE_INFINITY : Math.max(0, query.limit)
    const skip = Math.max(0, query.offset ?? 0)
    if (limit === 0) return []
    try {
      const tx = this.database.transaction('records', 'readonly')
      const index = tx.store.index('by-kind-committed')
      const collected: StoredRecord[] = []
      let cursor = await index.openCursor(
        IDBKeyRange.upperBound([query.kind, ABOVE_ALL_TIMESTAMPS]),
        'prev',
      )
      if (cursor !== null && skip > 0) cursor = await cursor.advance(skip)
      while (cursor !== null && collected.length < limit) {
        if (cursor.key[0] !== query.kind) break
        collected.push(cursor.value)
        cursor = await cursor.continue()
      }
      await tx.done
      return collected
    } catch (error) {
      throw mapStorageFailure(error, `could not read ${query.kind} records`)
    }
  }

  async latestCompletionForDocument(documentId: string): Promise<StoredRecord | null> {
    try {
      const tx = this.database.transaction('records', 'readonly')
      const index = tx.store.index('by-completion-document-committed')
      const cursor = await index.openCursor(
        IDBKeyRange.upperBound(['completion', documentId, ABOVE_ALL_TIMESTAMPS]),
        'prev',
      )
      const record =
        cursor !== null && cursor.key[0] === 'completion' && cursor.key[1] === documentId
          ? cursor.value
          : null
      await tx.done
      return record
    } catch (error) {
      throw mapStorageFailure(error, `could not read completion for document ${documentId}`)
    }
  }

  close(): void {
    this.database.close()
  }
}
